import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { invitationAPI } from '../services/api';
import './Auth.css';

function RegisterWithInvite() {
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');

  const [userData, setUserData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { registerWithInvite } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInvitation = async () => {
      if (!inviteCode) {
        setError('No invitation code provided');
        setLoading(false);
        return;
      }

      try {
        const response = await invitationAPI.get(inviteCode);
        const inv = response.data;

        if (!inv.is_valid) {
          if (inv.is_expired) {
            setError('This invitation has expired');
          } else if (inv.is_used) {
            setError('This invitation has already been used');
          } else if (inv.is_claimed) {
            setError('This family member has already been claimed');
          } else {
            setError('This invitation is no longer valid');
          }
        } else {
          setInvitation(inv);
        }
      } catch (err) {
        setError('Invalid invitation code');
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [inviteCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (userData.password !== userData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (userData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);

    try {
      await registerWithInvite({
        email: userData.email,
        password: userData.password,
        code: inviteCode,
      });
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>Loading invitation...</h2>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>Invalid Invitation</h2>
          {error && <div className="error-message">{error}</div>}
          <p style={{ color: 'var(--text-secondary)', marginTop: '16px' }}>
            This invitation link is not valid. Please contact the person who sent it to you.
          </p>
          <p className="auth-link">
            <Link to="/login">Go to Login</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>Join Family Tree</h2>

        <div style={{
          padding: '16px',
          background: 'var(--bg-tertiary)',
          borderRadius: '8px',
          marginBottom: '20px',
        }}>
          <p style={{ margin: '0 0 8px 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            You've been invited by <strong>{invitation.inviter_name}</strong> to claim:
          </p>
          <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: '600', color: 'var(--text-primary)' }}>
            {invitation.member_name}
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              value={userData.email}
              onChange={(e) => setUserData({ ...userData, email: e.target.value })}
              required
              autoFocus
            />
          </div>
          <div className="form-group">
            <label>Password *</label>
            <input
              type="password"
              value={userData.password}
              onChange={(e) => setUserData({ ...userData, password: e.target.value })}
              required
              minLength={6}
            />
          </div>
          <div className="form-group">
            <label>Confirm Password *</label>
            <input
              type="password"
              value={userData.confirmPassword}
              onChange={(e) => setUserData({ ...userData, confirmPassword: e.target.value })}
              required
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Creating Account...' : 'Create Account & Claim Profile'}
          </button>
        </form>

        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '16px' }}>
          By registering, you'll become the owner of {invitation.member_name}'s profile
          and gain access to the family tree.
        </p>

        <p className="auth-link">
          Already have an account? <Link to="/login">Login here</Link>
        </p>
      </div>
    </div>
  );
}

export default RegisterWithInvite;
